import { useCallback } from 'react';
import flag from '~/assets/icons/flag.svg';
import userRound from '~/assets/icons/user_round.svg';
import { Divider } from '~/components/ui/Divider';
import {
	NumberplatePreview,
	NumberPlatePreviewSize,
} from '~/components/ui/NumberplatePreview';
import { ScrollContainer } from '~/components/ui/ScrollContainer';
import { SectionHeading } from '~/components/ui/SectionHeading';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import {
	INicknameRequest,
	INumberplateRequest,
} from '~/rage/services/AdminService';
import {
	getVehicleNumberPlateTypeCaption,
	IAdminRequestModalRow,
	IModalStateAdminRequest,
	ModalButtonColor,
	ModalType,
} from '~/shared';
import { useVisibleRequestIds } from '../../useVisibleRequestIds';
import {
	StyledColumnContainer,
	StyledColumnHeadContainer,
	StyledContainer,
	StyledCounter,
	StyledRequestItem,
	StyledRequestNumber,
	StyledRequestPlayerID,
	StyledRequestPlayerName,
	StyledRequestPlayerNameChange,
	StyledRequestsList,
} from './RequestsTab.styled';

const REASON_PLACEHOLDER = 'Введите причину (Не обязательно)';

const requestModalDefaults = {
	reasonPlaceholder: REASON_PLACEHOLDER,
	acceptButton: {
		text: 'Одобрить заявку',
		color: ModalButtonColor.Success,
	},
	cancelButton: {
		text: 'Отменить заявку',
		color: ModalButtonColor.Danger2,
	},
} as const;

const buildNumberplateRows = (
	request: INumberplateRequest,
): IAdminRequestModalRow[] => {
	const rows: IAdminRequestModalRow[] = [
		{ kind: 'text', label: 'Никнейм игрока', value: request.playerName },
		{ kind: 'text', label: 'UID игрока', value: `#${request.playerId}` },
	];

	if (request.vehicleName) {
		rows.push({
			kind: 'text',
			label: 'Транспорт',
			value: request.vehicleName,
			...(request.vehicleEntityId != null && {
				valueSubtext: `ID:${request.vehicleEntityId}`,
			}),
		});
	}

	if (request.currentNumberplate != null) {
		rows.push({
			kind: 'numberplate',
			label: 'Текущий номер',
			secondaryLabel: getVehicleNumberPlateTypeCaption(
				request.currentNumberplate.type,
			),
			numberPlate: request.currentNumberplate,
		});
	}

	rows.push({
		kind: 'numberplate',
		label: 'Хочет установить',
		secondaryLabel: getVehicleNumberPlateTypeCaption(request.numberplate.type),
		numberPlate: request.numberplate,
	});

	return rows;
};

const buildNicknameRows = (
	request: INicknameRequest,
): IAdminRequestModalRow[] => [
	{ kind: 'text', label: 'Никнейм игрока', value: request.currentNickname },
	{ kind: 'text', label: 'UID игрока', value: `#${request.playerId}` },
	{ kind: 'text', label: 'Новый никнейм', value: request.newNickname },
];

const handleNumberplateRequestClick = async (
	request: INumberplateRequest,
): Promise<void> => {
	const reply = await rage.modal.showModalAsync<IModalStateAdminRequest>({
		type: ModalType.AdminRequest,
		title: 'Заявка на номер',
		rows: buildNumberplateRows(request),
		...requestModalDefaults,
	});

	if (reply.dismissedByClose) {
		return;
	}
	if (reply.isAccepted) {
		await rage.admin.approveNumberplateRequest(request.id, reply.reason);
	} else {
		await rage.admin.rejectNumberplateRequest(request.id, reply.reason);
	}

	void rage.admin.fetchRequests();
};

const handleNicknameRequestClick = async (
	request: INicknameRequest,
): Promise<void> => {
	const reply = await rage.modal.showModalAsync<IModalStateAdminRequest>({
		type: ModalType.AdminRequest,
		title: 'Заявка на никнейм',
		rows: buildNicknameRows(request),
		...requestModalDefaults,
	});

	if (reply.dismissedByClose) {
		return;
	}
	if (reply.isAccepted) {
		await rage.admin.approveNicknameRequest(request.id, reply.reason);
	} else {
		await rage.admin.rejectNicknameRequest(request.id, reply.reason);
	}

	void rage.admin.fetchRequests();
};

const RequestsTab = () => {
	const numberplateRequests = useObservable(
		rage.admin.getNumberplateRequests(),
	);
	const nicknameRequests = useObservable(rage.admin.getNicknameRequests());
	const markNumberplateRequestsViewed = useCallback((requestIds: string[]) => {
		return rage.admin.markModerationRequestsViewed({
			numberplateRequestIds: requestIds,
			nicknameRequestIds: [],
		});
	}, []);
	const markNicknameRequestsViewed = useCallback((requestIds: string[]) => {
		return rage.admin.markModerationRequestsViewed({
			numberplateRequestIds: [],
			nicknameRequestIds: requestIds,
		});
	}, []);
	const getNicknameRequestViewId = useCallback(
		(request: INicknameRequest) => request.viewId,
		[],
	);
	const {
		getRowRef: getNumberplateRequestRowRef,
		scrollContainerRef: numberplateScrollContainerRef,
	} = useVisibleRequestIds(numberplateRequests, markNumberplateRequestsViewed);
	const {
		getRowRef: getNicknameRequestRowRef,
		scrollContainerRef: nicknameScrollContainerRef,
	} = useVisibleRequestIds(
		nicknameRequests,
		markNicknameRequestsViewed,
		getNicknameRequestViewId,
	);

	return (
		<StyledContainer>
			<StyledColumnContainer>
				<StyledColumnHeadContainer>
					<SectionHeading text="Смена номера" icon={flag} />
					<StyledCounter>
						<Text>{numberplateRequests.length}</Text>
					</StyledCounter>
				</StyledColumnHeadContainer>
				<ScrollContainer
					height={476}
					scrollbarRight={5}
					showMask
					scrollContainerRef={numberplateScrollContainerRef}
				>
					<StyledRequestsList>
						{numberplateRequests.map((request) => (
							<StyledRequestItem
								key={request.id}
								ref={getNumberplateRequestRowRef(request.id)}
								onClick={() => {
									handleNumberplateRequestClick(request);
								}}
							>
								<StyledRequestNumber>
									<span>V</span> #{request.requestNumber}
								</StyledRequestNumber>
								<Divider $direction="V" />
								<StyledRequestPlayerName>
									{request.playerName} <span>#{request.playerId}</span>
								</StyledRequestPlayerName>
								<NumberplatePreview
									numberPlate={request.numberplate}
									size={NumberPlatePreviewSize.Size20}
								/>
							</StyledRequestItem>
						))}
					</StyledRequestsList>
				</ScrollContainer>
			</StyledColumnContainer>
			<Divider $direction="V" style={{ opacity: 0.1 }} />
			<StyledColumnContainer>
				<StyledColumnHeadContainer>
					<SectionHeading text="Смена никнейма" icon={userRound} />
					<StyledCounter>
						<Text>{nicknameRequests.length}</Text>
					</StyledCounter>
				</StyledColumnHeadContainer>
				<ScrollContainer
					height={476}
					scrollbarRight={5}
					showMask
					scrollContainerRef={nicknameScrollContainerRef}
				>
					<StyledRequestsList>
						{nicknameRequests.map((request) => (
							<StyledRequestItem
								key={request.id}
								ref={getNicknameRequestRowRef(request.viewId)}
								onClick={() => {
									handleNicknameRequestClick(request);
								}}
							>
								<StyledRequestNumber>
									<span>V</span> #{request.requestNumber}
								</StyledRequestNumber>
								<Divider $direction="V" />
								<StyledRequestPlayerNameChange>
									<span>{request.currentNickname} — </span>
									{request.newNickname}
								</StyledRequestPlayerNameChange>
								<StyledRequestPlayerID>
									#{request.playerId}
								</StyledRequestPlayerID>
							</StyledRequestItem>
						))}
					</StyledRequestsList>
				</ScrollContainer>
			</StyledColumnContainer>
		</StyledContainer>
	);
};

export { RequestsTab };
